import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { buyerMessagesAPI, formatApiError } from '../services/api';
import { toast } from 'sonner';
import PageShell from '../components/PageShell';
import { PillInput } from '../components/PillKit';
import { VoiceInputButton } from '../components/VoiceInputButton';
import { PaperPlaneTilt, ChatCircleDots, Storefront, CaretLeft } from '@phosphor-icons/react';

export default function BuyerMessagesPage() {
  const { user } = useAuth(); // eslint-disable-line no-unused-vars
  const [threads, setThreads] = useState([]);
  const [activeThread, setActiveThread] = useState(null);
  const [threadData, setThreadData] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [loading, setLoading] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadThreads(); }, []);

  const loadThreads = async () => {
    setLoading(true);
    try {
      const { data } = await buyerMessagesAPI.getThreads();
      setThreads(data);
    } catch (err) { toast.error(formatApiError(err)); }
    setLoading(false);
  };

  const openThread = async (threadId) => {
    setActiveThread(threadId);
    try {
      const { data } = await buyerMessagesAPI.getThread(threadId);
      setThreadData(data);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleReply = async () => {
    if (!replyText.trim() || !activeThread) return;
    try {
      await buyerMessagesAPI.reply(activeThread, replyText);
      setReplyText('');
      openThread(activeThread);
      loadThreads();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const timeAgo = (d) => {
    if (!d) return '';
    const diff = Math.floor((Date.now() - new Date(d).getTime()) / 1000);
    if (diff < 60) return 'now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
  };

  // ====== Mobile: active thread view ======
  if (activeThread && threadData) {
    return (
      <PageShell testId="message-thread" back>
        <button
          onClick={() => { setActiveThread(null); setThreadData(null); }}
          className="text-white/60 text-sm mb-3 flex items-center gap-1"
        >
          <CaretLeft className="w-4 h-4" /> Back to messages
        </button>

        <div className="bg-black border-[3px] border-white rounded-[18px] overflow-hidden flex flex-col" style={{ minHeight: '60vh' }}>
          {/* Header */}
          <div className="px-4 py-3 border-b-[2px] border-white/15 flex items-center gap-3">
            {threadData.vendor?.avatar_url ? (
              <img src={threadData.vendor.avatar_url} alt="" className="w-10 h-10 rounded-full" />
            ) : (
              <div className="w-10 h-10 rounded-full border-[2px] border-white flex items-center justify-center text-white text-sm font-extrabold">
                {threadData.vendor?.name?.[0]?.toUpperCase() || '?'}
              </div>
            )}
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-white truncate">{threadData.vendor?.name}</p>
              <p className="text-xs text-white/50 truncate">{threadData.thread?.subject}</p>
            </div>
            {threadData.vendor?.username && (
              <Link to={`/store/${threadData.vendor.username}`} className="text-[#22C55E] text-xs font-bold flex items-center gap-1 hover:underline">
                <Storefront className="w-3.5 h-3.5" /> Store
              </Link>
            )}
          </div>

          {/* Messages */}
          <div className="p-4 flex-1 overflow-y-auto space-y-3" data-testid="message-list">
            {threadData.messages?.map((m) => (
              <div key={m.message_id} className={`flex ${m.sender_type === 'buyer' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${
                  m.sender_type === 'buyer'
                    ? 'bg-[#22C55E] text-black'
                    : 'bg-white/8 text-white border-[2px] border-white/15'
                }`}>
                  <p className="text-sm">{m.content}</p>
                  <p className="text-[10px] opacity-60 mt-1">{timeAgo(m.created_at)}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Reply pill */}
          <div className="border-t-[2px] border-white/15 p-3 flex gap-2 items-center">
            <input
              type="text"
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="Type a reply…"
              className="flex-1 bg-transparent border-[3px] border-white rounded-full px-5 py-3 text-white text-sm placeholder:text-white/40 focus:outline-none focus:border-[#22C55E]"
              data-testid="buyer-reply-input"
              onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleReply(); } }}
            />
            <VoiceInputButton onTranscript={(text) => setReplyText((p) => p ? p + ' ' + text : text)} />
            <button
              onClick={handleReply}
              className="w-12 h-12 rounded-full bg-[#22C55E] text-black flex items-center justify-center active:scale-[0.95] transition-transform"
              data-testid="buyer-send-reply"
              aria-label="Send"
            >
              <PaperPlaneTilt className="h-5 w-5" weight="fill" />
            </button>
          </div>
        </div>
      </PageShell>
    );
  }

  // ====== Thread list ======
  return (
    <PageShell testId="buyer-messages-page" title={{ line1: 'Messages', line2: 'Your vendor conversations' }}>
      {loading ? (
        <div className="text-center py-12 text-white/50">Loading…</div>
      ) : threads.length === 0 ? (
        <div className="text-center py-16 bg-black border-[3px] border-white rounded-[18px]">
          <ChatCircleDots className="w-14 h-14 mx-auto text-white/30 mb-3" weight="fill" />
          <p className="text-white/60 text-sm">No messages yet.</p>
          <p className="text-white/40 text-xs mt-1">Vendors will appear here when they message you.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {threads.map((t) => (
            <button
              key={t.thread_id}
              onClick={() => openThread(t.thread_id)}
              className="w-full bg-black border-[3px] border-white rounded-[18px] px-4 py-3 flex items-center gap-3 active:scale-[0.98] transition-transform text-left"
              data-testid={`buyer-thread-${t.thread_id}`}
            >
              {t.vendor?.avatar_url ? (
                <img src={t.vendor.avatar_url} alt="" className="w-12 h-12 rounded-full flex-shrink-0" />
              ) : (
                <div className="w-12 h-12 rounded-full border-[2px] border-[#22C55E] flex items-center justify-center text-[#22C55E] text-base font-extrabold flex-shrink-0">
                  {t.vendor?.name?.[0]?.toUpperCase() || '?'}
                </div>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex items-center justify-between gap-2 mb-0.5">
                  <p className="text-sm font-bold text-white truncate">{t.vendor?.name || 'Vendor'}</p>
                  <span className="text-[10px] text-white/40 flex-shrink-0">{timeAgo(t.updated_at)}</span>
                </div>
                <p className="text-xs text-[#22C55E] font-bold truncate">{t.subject}</p>
                <p className="text-xs text-white/50 truncate mt-0.5">{t.last_message}</p>
              </div>
            </button>
          ))}
        </div>
      )}
    </PageShell>
  );
}
